import { useContext, useEffect, useMemo } from 'react'
import { NavLink, Outlet, useLocation, useMatches, useParams } from 'react-router'

import config from 'config'

import Link from 'tools/Link'
import Loading from 'tools/Loading'
import { Icon } from 'tools/Uniform'

import { SubTabs } from 'app/Nav'
import { SITEMAP, TABS } from 'libreon/routes'
import GlobalContext, { R_GLOBAL } from 'reducer/global'

import style from './index.module.scss'

// get the top level routeId when it is no longer in context
export function useRouteHead() {
  const matches = useMatches()
  return matches[matches.length - 1]
}

////////////////////////////////////////////////////////////////////////////////
function mapVisible({ list, params, ...rest }) {
  ///, routeId, user, states, origin}) {
  const data = {
    ...params,
    ...rest
    // routeId,
    // user,
    // states,
    // origin
  }
  if (!list) return []
  return list.reduce((out, id) => {
    const args = enrichTabData(data, id)
    if (isHidden(args)) return out
    return out.concat({ id, args })
  }, [])
}

////////////////////////////////////////////////////////////////////////////////
// iterate only the list
export function useVisible(list, origin) {
  const [s] = useContext(GlobalContext)
  const { user, tabs: states } = s
  const params = useParams()
  const { id: routeId } = useRouteHead()

  return useMemo(
    () => mapVisible({ list, params, routeId, user, states, origin }),
    [list, params, routeId, user, states, origin]
  )
}

// iterate the list, making them dividers, then expand its children
export function useExpandVisible(list, origin) {
  const [{ user, tabs: states }] = useContext(GlobalContext)
  const params = useParams()
  const { id: routeId } = useRouteHead()

  return useMemo(
    () =>
      mapVisible({ list, params, routeId, user, states, origin }).reduce(
        (out, { id, args }) => {
          const kidList = TABS[id]?.items || SITEMAP[id].children
          return out.concat(menuDivider(id, args)).concat(
            mapVisible({
              list: kidList,
              params,
              routeId,
              user,
              states,
              origin: id
            })
          )
        },
        []
      ),
    [list, params, routeId, user, states, origin]
  )
}

function menuDivider(id, args) {
  return { id, args: { ...args, divider: true } }
}

////////////////////////////////////////////////////////////////////////////////
export function getNavPoint(navId) {
  // no id is to unset it
  if (!navId) return undefined
  const point = SITEMAP[navId]
  if (!point) throw new Error(`Nav point not found in sitemap: ${navId}`)
  return point
}

////////////////////////////////////////////////////////////////////////////////
function enrichTabData(meta, navId) {
  const tab = SITEMAP[navId]
  if (!tab) throw new Error(`TabId not found in sitemap: ${navId}`)

  const active = meta.routeId === navId
  return { ...meta, tabId: navId, navId, tab, active }
}

function isHidden(data) {
  const nav = getNavPoint(data.navId).nav
  return !nav || nav.hide || (nav.visible && !nav.visible(data))
}

export function StatefulLink({
  navId,
  tabData,
  name,
  innerClass = 'relative f3 ph2',
  className = 'button tab',
  activeClass = 'neutral current',
  inactiveClass = 'clear',
  Type = NavLink,
  ...rest
}) {
  const elem = getNavPoint(navId)
  const dest = elem.pathTo({ ...tabData, ...rest })
  className = className + ' ' + (tabData.active ? activeClass : inactiveClass)
  return (
    <Link Type={Type} to={dest} className={className}>
      <div className={innerClass}>{name}</div>
    </Link>
  )
}

// ph2 pv1"
export function NavButton({
  Type = Link,
  icon,
  img,
  children,
  active = false,
  ...args
}) {
  return (
    <Link
      className={`button flex flex-column items-center fw3 f1 relative ph2 pv1 ${active ? 'neutral' : 'clear'}`}
      Type={Type}
      {...args}
    >
      {img ? img : icon && <Icon type={icon} className="mb1 f4" />}
      {children}
    </Link>
  )
}

export function NotificationBadge() {
  const [
    {
      notify: { navbar: navNotify }
    }
  ] = useContext(GlobalContext)

  if (!navNotify) return null

  return (
    <div className="flex-center ml-auto f3 i truncate">{navNotify.content}</div>
  )
}

/*

routeId -> the route I came in on (highest level)
navId -> the navPoint ID we'll be using (in cases where routeId is an alias)

*/
export function useNav(routeId, sectionId) {
  const [{ page }, dispatch] = useContext(GlobalContext)
  const navId = getNavPoint(routeId)?.nav?.uses || routeId

  if (routeId && page.navId !== navId && !page.subId)
    document.title = getTitle(navId)

  useEffect(() => {
    let value = { routeId, navId }
    if (sectionId) value.sectionId = sectionId

    dispatch({ type: R_GLOBAL.PAGE_SET, value })
  }, [dispatch, navId, routeId, sectionId])
}

export function useSection(sectionId) {
  const [, dispatch] = useContext(GlobalContext)

  useEffect(
    () => dispatch({ type: R_GLOBAL.PAGE_MERGE, value: { sectionId } }),
    [dispatch, sectionId]
  )
}

export function useSub(subId) {
  const [{ page }, dispatch] = useContext(GlobalContext)

  // I tried to inject parent's navId.title(), but because of the async and
  // disorderly way both useNav and useSub are called, it wasn't reliable
  // enough to include the parent in the title without crazy gymnastics -BJG
  if (subId && page.subId !== subId) document.title = getTitle(subId)

  useEffect(
    () => dispatch({ type: R_GLOBAL.PAGE_SET, value: { subId } }),
    [dispatch, subId]
  )
}

function getTitle(navId) {
  const title = getNavPoint(navId).title()
  if (title) return `${title} - ${config.name}`
  return config.name
}

export function NavHeading({
  navId,
  heading = undefined, // override what's in navId; but still need navId
  icon = undefined, // override icon
  label, // after the heading  decorate = true,
  decorate = true,
  embed = false,
  loading = false,
  children,
  backTo
}) {
  const sec = getNavPoint(navId) || {}
  const { nav = {} } = sec
  const { search } = useLocation()

  //
  const titleClasses = ['flex-items gh2']
  if (!embed && decorate) {
    titleClasses.push('f5') // eslint-disable-line no-restricted-syntax
    if (nav.color) titleClasses.push(nav.color) // eslint-disable-line no-restricted-syntax
  }
  if (loading) titleClasses.push('minimize') // eslint-disable-line no-restricted-syntax

  if (!icon && nav.icon) icon = nav.icon

  //
  const wrapClasses = ['flex items-end mb2 gh3']
  if (!embed) wrapClasses.push('hmin3')

  return (
    <div className={wrapClasses.join(' ')}>
      <div className={titleClasses.join(' ')}>
        {backTo && (
          <div className={style.back}>
            <Link
              to={backTo.includes('?') ? backTo : backTo + search}
              className="button neutral pill flex-center f3"
            >
              <Icon type="back" />
            </Link>
          </div>
        )}
        {!embed && icon && <Icon type={icon} />}
        <span className="fw1">{heading || sec.title()}</span>
      </div>
      {label}
      {loading && (
        <div className="f3 mb1">
          <Loading babble={false} wait={0} />
        </div>
      )}
      <div className="ml-auto flex items-stretch gh1">{children}</div>
    </div>
  )
}

export function NavOutlet({ routeId }) {
  useNav(routeId)
  return <Outlet />
}

export function SubTabOutlet({ routeId }) {
  useSub(routeId)
  // const title = useMemo(() => SITEMAP[routeId].title(), [routeId])
  return (
    <>
      <SubTabs navId={routeId} title={false} />
      <Outlet />
    </>
  )
}
